import React, { useState, useEffect } from 'react';
import { createBlankDocument } from '../api/documentApi';
import { useLanguage } from '../i18n/LanguageContext';
import './AddDocumentModal.css';

interface AddDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (documentId: number) => void;
  folderId?: number | null; // create straight into this folder
}

const AddDocumentModal: React.FC<AddDocumentModalProps> = ({ isOpen, onClose, onSuccess, folderId = null }) => {
  const { t } = useLanguage();
  const [documentName, setDocumentName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setDocumentName('');
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    setError(null);

    if (!documentName.trim()) {
      setError(t.addDocument.nameRequired);
      return;
    }

    setIsSubmitting(true);

    try {
      // Create empty document with just the name
      const document = await createBlankDocument(documentName.trim(), '', folderId);

      onSuccess(document.id);
      onClose();
    } catch (err: any) {
      console.error('Error creating document:', err);
      setError(err.response?.data?.error || t.addDocument.failedToCreate);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="add-document-modal-overlay" onClick={onClose}>
      <div className="add-document-modal" onClick={(e) => e.stopPropagation()}>
        <div className="add-document-header">
          <button className="cancel-btn" onClick={onClose}>{t.addDocument.cancel}</button>
          <h2>{t.addDocument.title}</h2>
          <button 
            className="create-btn" 
            onClick={handleSubmit}
            disabled={isSubmitting || !documentName.trim()}
          >
            {isSubmitting ? t.addDocument.creating : t.addDocument.create}
          </button>
        </div>

        {error && <div className="add-document-error">{error}</div>}

        <div className="add-document-body">
          <input
            type="text"
            placeholder={t.addDocument.documentNamePlaceholder}
            value={documentName}
            onChange={(e) => setDocumentName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && documentName.trim()) {
                handleSubmit();
              }
            }}
            className="document-name-input"
            autoFocus
          />
        </div>
      </div>
    </div>
  );
};

export default AddDocumentModal;
